import React, { useState } from "react";
import { CloudUploadIcon, CheckCircleIcon } from "../../../Icons";
import {
  DEFAULT_DOCUMENT_MAX_BYTES,
  formatMaxFileSizeLabel,
} from "../hooks/useDocumentFileUpload";
import { DOCUMENT_THEME } from "../documentTheme";

/**
 * Dashed upload area — Patient-branded, used by credential/tax document modals.
 */
export default function DocumentFileDropzone({
  inputId,
  accept = ".pdf,.jpg,.jpeg,.png,.heic,.heif,image/heic,image/heif",
  typesLabel = "PDF, JPG, PNG, HEIC",
  maxBytes = DEFAULT_DOCUMENT_MAX_BYTES,
  fileName,
  onFileChange,
  onFileDrop,
  onDragOver,
  onClear,
  hasError,
  selectedNamePrefix = "",
}) {
  const limitText = formatMaxFileSizeLabel(maxBytes);
  const [isDragging, setIsDragging] = useState(false);

  const borderTone = fileName
    ? "border-emerald-300 bg-emerald-50/60"
    : isDragging
      ? "border-indigo-400 bg-indigo-50/60"
      : hasError
        ? "border-red-300 bg-red-50/60"
        : "border-indigo-200 bg-indigo-50/30 hover:border-indigo-300";

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    onDragOver?.(e);
    setIsDragging(true);
  };

  const handleDrop = (e) => {
    setIsDragging(false);
    onFileDrop?.(e);
  };

  return (
    <div
      className={`mt-1 flex justify-center px-4 py-4 border border-dashed rounded-xl transition-colors ${borderTone}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="space-y-1.5 text-center pointer-events-none">
        {fileName ? (
          <CheckCircleIcon className="mx-auto h-8 w-8 text-emerald-500" />
        ) : (
          <CloudUploadIcon className="mx-auto h-8 w-8 text-indigo-400" />
        )}
        <div className="flex text-sm text-slate-600 justify-center flex-wrap gap-x-1 pointer-events-auto">
          <label
            htmlFor={inputId}
            className={`relative cursor-pointer rounded-md font-medium ${DOCUMENT_THEME.button.link}`}
          >
            <span>{fileName ? "Change file" : "Upload a file"}</span>
            <input
              id={inputId}
              name={inputId}
              type="file"
              className="sr-only"
              onChange={onFileChange}
              accept={accept}
            />
          </label>
          {fileName ? (
            <>
              <span className="text-slate-300">|</span>
              <button
                type="button"
                onClick={onClear}
                className="font-medium text-red-600 hover:text-red-700 hover:underline"
              >
                Remove
              </button>
            </>
          ) : (
            <span className="text-slate-500">or drag and drop</span>
          )}
        </div>
        <p className="text-[11px] text-slate-500">
          {fileName ? (
            <span className="text-emerald-700 font-medium block">
              {selectedNamePrefix}
              {fileName}
            </span>
          ) : (
            `${typesLabel} up to ${limitText}`
          )}
        </p>
      </div>
    </div>
  );
}
